import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { injectIntl, intlShape } from "@ai360/core/intl";
import { v4 as uuid } from "uuid";
import { AgBytesAPI, OrgLevelProductsAPI } from "@ai360/core";

import { ProductList } from "~/admin/setup/equation-list/components";
import { messages } from "~/admin/setup/i18n-messages";
import { Button, Loader, LoaderTypes, Section, SelectInput, SubSection } from "~/core";
import SlidingPanel from "~/sliding-panel/sliding-panel";

import { OrgLevelList } from "./components/org-level-list";

type IHierarchyFilterLevelWithParentsGuid =
    OrgLevelProductsAPI.IHierarchyFilterLevelWithParentsGuid;
type IOrgLevelProductProduct = OrgLevelProductsAPI.IOrgLevelProductProduct;
type IUserCompanyList = OrgLevelProductsAPI.IUserCompanyList;
type AgBytesItem = AgBytesAPI.AgBytesItem;

type OrgLevelProductsAddEditPanelProps = {
    closePanel: () => void;
    intl: intlShape;
    selectedRow: OrgLevelProductsAPI.IOrgLevelProductGridRow | null;
};

const OrgLevelProductsAddEditPanel_ = ({
    closePanel,
    intl: { formatMessage },
    selectedRow,
}: OrgLevelProductsAddEditPanelProps): ReactElement => {
    const [selectedCompanyUserGuid, setSelectedCompanyUserGuid] = useState(null as string | null);
    const [selectedLocations, setSelectedLocations] = useState(
        [] as IHierarchyFilterLevelWithParentsGuid[]
    );
    const [selectedProducts, setSelectedProducts] = useState([] as IOrgLevelProductProduct[]);

    const [mergeOrgLevelProduct, { isLoading: isMergeLoading }] =
        OrgLevelProductsAPI.useMergeOrgLevelProductMutation();

    const {
        data: companyData = { model: [] as IUserCompanyList[] },
        isFetching: isCompanyLoading,
    } = OrgLevelProductsAPI.useGetUserCompanyListQuery();

    const [lazyLoadCompany, { isFetching: isLazyCompanyLoading }] =
        OrgLevelProductsAPI.useLazyGetOrgLevelListWithParentsGuidQuery();

    const {
        data: locationData = { model: [] as IHierarchyFilterLevelWithParentsGuid[] },
        isFetching: isLocationLoading,
    } = OrgLevelProductsAPI.useGetOrgLevelListWithParentsGuidQuery(selectedCompanyUserGuid, {
        skip: !selectedCompanyUserGuid,
    });

    const { data: productData = { model: [] as AgBytesItem[] }, isFetching: isProductLoading } =
        OrgLevelProductsAPI.useGetProductsListForOrgLevelProductQuery();

    useEffect(() => {
        if (!selectedRow || productData.model.length === 0 || companyData.model.length === 0)
            return;
        setSelectedProducts(selectedRow.products.map((p) => ({ ...p })));

        const orgLevelGuids = new Set(selectedRow.orgLevels.map((l) => l.orgLevelGuid));
        const parentOrgLevels = new Set(selectedRow.orgLevels.map((l) => l.parentOrgLevelGuid));
        const companyUserGuids = Array.from(
            new Set(
                companyData.model
                    .filter((c) => parentOrgLevels.has(c.companyGuid))
                    .map((c) => c.userGuid)
            )
        );
        if (companyUserGuids.length === 1) {
            setSelectedCompanyUserGuid(companyUserGuids[0]);
        }
        const results = companyUserGuids.map((userGuid) => {
            return lazyLoadCompany(userGuid);
        });
        Promise.all(results).then((values) => {
            const locations = values
                .map((value) => value.data.model)
                .flat()
                .filter((location) => orgLevelGuids.has(location.orgLevelGuid))
                .map((location) => ({ ...location }));
            setSelectedLocations(locations);
        });
    }, [selectedRow, productData, companyData]);

    const mutableLocationData = useMemo(() => {
        return locationData.model.map((location) => ({ ...location }));
    }, [locationData]);

    const locationOptions = useMemo(() => {
        const locationGuids = new Set<string>(
            mutableLocationData.map((location) => location.orgLevelGuid)
        );
        const missingFromSelected = selectedLocations.filter((location) => {
            return !locationGuids.has(location.orgLevelGuid);
        });
        return [...missingFromSelected, ...mutableLocationData];
    }, [selectedLocations, mutableLocationData]);

    const isLoading =
        isCompanyLoading ||
        isLocationLoading ||
        isProductLoading ||
        isMergeLoading ||
        isLazyCompanyLoading;

    const onSave = () => {
        const mergeModel = {
            productOrgLevelGuid: selectedRow ? selectedRow.productOrgLevelGuid : uuid(),
            active: true,
            products: selectedProducts.map((product) => ({
                productGuid: product.productGuid,
                name: product.name,
            })),
            orgLevels: selectedLocations.map((location) => ({
                orgLevelGuid: location.orgLevelGuid,
                name: location.orgLevelName,
                parentOrgLevelGuid:
                    location.orgLevelParents.length > 0 ? location.orgLevelParents[0].guid : null,
            })),
        };

        mergeOrgLevelProduct(mergeModel)
            .unwrap()
            .then(() => {
                closePanel();
            })
            .catch(() => {
                // Handled by ApiResultsWrapper
            });
    };

    const addEditPanel = () => (
        <div className="add-edit-panel org-level-products-add-edit">
            {!isLoading ? null : <Loader type={LoaderTypes.LINE_SCALE} />}
            <div className="section-container">
                <Section
                    required
                    className="grid-section"
                    headerText={formatMessage(messages.productsHeader)}
                >
                    <SubSection>
                        <ProductList
                            formatMessage={formatMessage}
                            options={productData.model}
                            record={selectedProducts}
                            onTextChange={(e, value) => {
                                setSelectedProducts([...value]);
                            }}
                            showInactiveProducts={true}
                        />
                    </SubSection>
                </Section>
                <span className="bar section-fence" />
                <Section
                    required
                    className="grid-section"
                    headerText={formatMessage(messages.orgLevelList)}
                >
                    <SubSection className="select-company-container">
                        <div className="select-company-message">
                            {formatMessage(messages.selectACompanyMessage)}
                        </div>
                        <SelectInput
                            clearable={false}
                            onChange={setSelectedCompanyUserGuid}
                            options={companyData.model.map(({ userGuid, companyName }) => ({
                                value: userGuid,
                                label: companyName,
                            }))}
                            placeholderText={formatMessage(messages.companyName)}
                            required
                            value={selectedCompanyUserGuid}
                        />
                    </SubSection>
                    <OrgLevelList
                        orgLevels={locationOptions}
                        onSelectionChange={setSelectedLocations}
                        selectedOrgLevels={selectedLocations}
                    />
                </Section>
            </div>
        </div>
    );

    return (
        <SlidingPanel
            component={addEditPanel}
            navigateTo={{ parentNameCode: "101", childNameCode: "274" }}
        >
            <Button
                type="save"
                forceSubmit
                disabled={selectedProducts.length === 0 || selectedLocations.length === 0}
                onClick={onSave}
            />
            <Button type="cancel" className="user-cancel" onClick={closePanel} />
        </SlidingPanel>
    );
};

export const OrgLevelProductsAddEditPanel = injectIntl(OrgLevelProductsAddEditPanel_);
